// Sends one (possibly very large) file to /api/sales/my/uploads in pieces, with progress. A failed piece is retried
// a few times before giving up, so a brief network drop doesn't lose a multi-gigabyte drawing package.
import { api } from '../../lib/api'

export type UploadKind = 'site_plan' | 'drawing' | 'noise_report'
export interface UploadedFile { id: number; name: string; size: number; kind: UploadKind }
export const MAX_UPLOAD_BYTES = 5 * 1024 ** 3

export async function uploadLargeFile(file: File, kind: UploadKind, onProgress: (sentBytes: number) => void, signal?: AbortSignal): Promise<UploadedFile> {
  if (file.size > MAX_UPLOAD_BYTES) throw new Error(`${file.name} is larger than 5 GB. Please share it as a link instead.`)
  if (!file.size) throw new Error(`${file.name} is empty.`)
  const start = await api.post<{ ticket: string; partSize: number; parts: number }>('/api/sales/my/uploads', { fileName: file.name, size: file.size, kind })
  const etags: string[] = []
  try {
    for (let n = 1; n <= start.parts; n++) {
      const piece = file.slice((n - 1) * start.partSize, Math.min(file.size, n * start.partSize))
      etags.push(await sendPiece(start.ticket, n, piece, signal))
      onProgress(Math.min(file.size, n * start.partSize))
    }
    const done = await api.post<{ fileId: number; fileName: string; size: number; kind: UploadKind }>('/api/sales/my/uploads/complete', { ticket: start.ticket, etags })
    return { id: done.fileId, name: done.fileName, size: done.size, kind: done.kind }
  } catch (e) {
    void api.post('/api/sales/my/uploads/abort', { ticket: start.ticket }).catch(() => undefined)
    throw e
  }
}

async function sendPiece(ticket: string, n: number, piece: Blob, signal?: AbortSignal): Promise<string> {
  let last: unknown
  for (let attempt = 0; attempt < 4; attempt++) {
    if (signal?.aborted) throw new Error('Upload cancelled.')
    try {
      const r = await fetch(`/api/sales/my/uploads/part?ticket=${encodeURIComponent(ticket)}&n=${n}`, { method: 'PUT', credentials: 'same-origin', headers: { 'X-Briary': '1', 'Content-Type': 'application/octet-stream' }, body: piece, signal })
      const body = (await r.json().catch(() => ({}))) as { etag?: string; message?: string }
      if (r.ok && body.etag) return body.etag
      // The server refused this piece for a reason a retry won't fix.
      if (r.status >= 400 && r.status < 500 && r.status !== 408 && r.status !== 429) throw Object.assign(new Error(body.message ?? 'The upload was refused.'), { final: true })
      last = new Error(body.message ?? `Upload failed (${r.status}).`)
    } catch (e) {
      if ((e as { final?: boolean }).final || signal?.aborted) throw e
      last = e
    }
    await new Promise((res) => setTimeout(res, 1500 * (attempt + 1)))
  }
  throw last instanceof Error ? last : new Error('The upload failed. Please try again.')
}
